import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { Box } from '@mui/material'
import { Add } from '@mui/icons-material'
import { TProduct } from 'types'
import { RootState } from 'store'
import { Button } from 'components/ui'
import { successToast } from 'utils/toastService'
import { ProductsTableColumns } from './ProductTableColumns'
import useEditModal from '../dialogs/useEditModal'
import {
  CrudAction,
  Entity,
  usePageHeader,
  usePagination,
  usePermissions,
} from '../../../../../utils/hooks'
import * as sxStyles from './Products.styled'
import { useCallback, useMemo } from 'react'
import { ProductsModuleAdapter } from '../types/shared.types'

interface IProductsProps {
  adapter: ProductsModuleAdapter
}

const Products = ({ adapter }: IProductsProps) => {
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()

  const tableKey = adapter.tableKey ?? 'blendingSpec'

  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination({ tableKey })

  const { data: productsResp, isLoading: isProductsLoading } =
    adapter.useProducts({
      currentPage,
      itemsPerPage: rowsPerPage,
    })

  // get default attributes
  const { data: defaultAttributes } = adapter.useDefaultAttributes()

  const validationRules = useMemo(() => {
    return adapter.computeValidationRules
      ? adapter.computeValidationRules(defaultAttributes?.results)
      : undefined
  }, [adapter.computeValidationRules, defaultAttributes?.results])

  const { data: categoriesData } = adapter.useCategories
    ? adapter.useCategories()
    : { data: undefined }

  const memoizedCategoriesResults = useMemo(() => {
    return categoriesData?.results
  }, [categoriesData?.results])

  const initialAttrs = useMemo(() => {
    return adapter.mapAttributesForNewItem
      ? adapter.mapAttributesForNewItem(defaultAttributes?.results)
      : defaultAttributes?.results?.map((attribute) => ({
          name: attribute.name,
          range_min: 0,
          range_max: 0,
          min_value: attribute.min_value || 0,
          max_value: attribute.max_value || 0,
        }))
  }, [adapter.mapAttributesForNewItem, defaultAttributes?.results])

  const createProduct = adapter.useCreate()

  const createProductMutation = useMutation(createProduct, {
    onSuccess: () => {
      void queryClient.invalidateQueries([`${adapter.invalidatQueryName}`])
      void queryClient.invalidateQueries(['products_list_dropdown'])
      successToast(adapter.toasts.created)
      toggleAddModal()
      resetAddModal()
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : adapter.toasts.updateFailed)
      console.error('Failed to create blending spec:', error)
    },
  })

  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )

  const [addModal, toggleAddModal, resetAddModal] = useEditModal({
    initialData: useMemo(
      () => ({
        name: '',
        attributes: initialAttrs,
        company_id: companyId,
      }),
      [initialAttrs, companyId]
    ),
    onConfirm: useCallback(
      async (data: TProduct) => {
        createProductMutation.mutate({
          ...data,
          company_id: companyId,
        })
        await queryClient.invalidateQueries([`${adapter.invalidatQueryName}`])
      },
      [createProductMutation, companyId, queryClient]
    ),
    mode: 'add',
    titleEdit: `Edit ${adapter?.headerTitle}`,
    titleAdd: `Add ${adapter?.headerTitle}`,
    namePlaceholderAdd: `${adapter?.headerTitle} Name`,
    validationRules,
    categoriesWithAttributes: memoizedCategoriesResults,
  })

  const actionButton = useMemo(
    () => (
      <Button
        startIcon={<Add />}
        onClick={toggleAddModal}
        disabled={
          !checkPermission(
            adapter.permissionEntity ?? Entity.PRODUCT,
            CrudAction.CREATE
          )
        }
      >
        {adapter.addButtonText}
      </Button>
    ),
    [
      toggleAddModal,
      checkPermission,
      adapter.permissionEntity,
      adapter.addButtonText,
    ]
  )

  usePageHeader({
    headerTitle: adapter.headerTitle,
    actionButton,
  })

  return (
    <Box sx={sxStyles.productsBoxWrapper}>
      <ProductsTableColumns
        products={productsResp?.results || []}
        pagination={productsResp?.pagination}
        isProductsLoading={isProductsLoading}
        attributes={defaultAttributes?.results || []}
        setBlendingSpecCurrentPage={setCurrentPage}
        setBlendingSpecRowsPerPage={setRowsPerPage}
        tableKey={tableKey}
        adapter={adapter}
      />
      {addModal}
    </Box>
  )
}

export default Products
